/**
 * Upload queue helpers for the upload page: a rolling pool that keeps a fixed
 * number of uploads in flight, and one upload with stall/response timeouts and
 * a small retry policy. No React here, so both can be tested with fake timers.
 */

/**
 * Body phase: abort when the browser reports no upload progress (bytes sent)
 * for this long. The timer restarts on every progress event, so a slow but
 * moving upload is never killed. There is no total timeout.
 */
export const UPLOAD_STALL_TIMEOUT_MS = 60_000;

/**
 * Response phase: once all bytes are sent the server still checks pages and
 * stores the file before it answers (20-45 s seen in production). Abort when
 * no response arrives within this time.
 */
export const UPLOAD_RESPONSE_TIMEOUT_MS = 120_000;

/** Wait before the 2nd and 3rd attempt (so 3 attempts in total). */
export const UPLOAD_RETRY_DELAYS_MS = [2_000, 5_000];

export type UploadPool<K> = {
  /** Queue a job unless the same key is already queued or running. */
  add: (key: K, job: () => Promise<unknown>) => boolean;
  has: (key: K) => boolean;
};

type UploadPoolOptions = {
  /** Called with true when the first job is queued, false when all settled. */
  onActiveChange?: (active: boolean) => void;
};

/**
 * Runs up to `concurrency` jobs; starts the next one as soon as any settles.
 * Not tied to a component: queued and running jobs go on until done, and an
 * idle pool holds no references to them.
 */
export const createUploadPool = <K>(
  concurrency: number,
  { onActiveChange }: UploadPoolOptions = {}
): UploadPool<K> => {
  const queue: { key: K; job: () => Promise<unknown> }[] = [];
  const running = new Set<K>();
  let active = false;

  const setActive = (next: boolean) => {
    if (next === active) return;
    active = next;
    onActiveChange?.(next);
  };

  const pump = () => {
    while (running.size < concurrency && queue.length > 0) {
      const { key, job } = queue.shift()!;
      running.add(key);
      const done = () => {
        running.delete(key);
        pump();
      };
      let promise: Promise<unknown>;
      try {
        promise = Promise.resolve(job());
      } catch (error) {
        promise = Promise.reject(error);
      }
      promise.then(done, done);
    }
    setActive(running.size > 0 || queue.length > 0);
  };

  const has = (key: K) => running.has(key) || queue.some((entry) => entry.key === key);

  return {
    add: (key, job) => {
      if (has(key)) return false;
      queue.push({ key, job });
      pump();
      return true;
    },
    has,
  };
};

export type UploadProgress = { loaded: number; total?: number };

export type UploadResult<T> =
  | { kind: 'done'; value: T }
  | { kind: 'cancelled' }
  | {
      kind: 'failed';
      error: unknown;
      status?: number;
      /** All bytes were sent, so the server may have created the task. */
      maybeUploaded: boolean;
    };

type UploadWithRetryOptions<T> = {
  send: (
    abortController: AbortController,
    onUploadProgress: (event: UploadProgress) => void
  ) => Promise<T>;
  /** Called with a fresh controller before each attempt (and its backoff). */
  onAttempt?: (abortController: AbortController, attempt: number) => void;
  onProgress?: (event: UploadProgress) => void;
  stallTimeoutMs?: number;
  responseTimeoutMs?: number;
  retryDelaysMs?: number[];
};

/** Same cancel check as before: axios CanceledError, as mapped by baseQuery. */
export const isCancelError = (error: any) =>
  error?.code === 'ERR_CANCELED' || error?.data === 'canceled';

const statusOf = (error: any): number | undefined =>
  typeof error?.status === 'number' ? error.status : undefined;

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<boolean>((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });

/**
 * One file upload. Retries network errors, our own timeouts and 5xx, but only
 * while the body was still being sent: the backend creates the task after the
 * whole body arrived and has no idempotency key, so a failure after the last
 * byte may already have created it. Never retries 4xx or a user cancel.
 * If any progress event has no usable total, we cannot tell when the body is
 * done, so that attempt is treated like the response phase: no auto-retry.
 */
export const uploadWithRetry = async <T>({
  send,
  onAttempt,
  onProgress,
  stallTimeoutMs = UPLOAD_STALL_TIMEOUT_MS,
  responseTimeoutMs = UPLOAD_RESPONSE_TIMEOUT_MS,
  retryDelaysMs = UPLOAD_RETRY_DELAYS_MS,
}: UploadWithRetryOptions<T>): Promise<UploadResult<T>> => {
  for (let attempt = 1; ; attempt++) {
    const abortController = new AbortController();
    onAttempt?.(abortController, attempt);
    if (attempt > 1 && !(await wait(retryDelaysMs[attempt - 2], abortController.signal))) {
      return { kind: 'cancelled' };
    }

    let bodyComplete = false;
    let completionUnknown = false;
    let timedOut = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = (ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timedOut = true;
        abortController.abort();
      }, ms);
    };

    arm(stallTimeoutMs);
    try {
      const value = await send(abortController, (event) => {
        if (bodyComplete || timedOut) return;
        onProgress?.(event);
        const hasTotal = typeof event.total === 'number' && event.total > 0;
        if (!hasTotal) completionUnknown = true;
        if (hasTotal && event.loaded >= event.total!) {
          bodyComplete = true;
          arm(responseTimeoutMs);
        } else {
          arm(stallTimeoutMs);
        }
      });
      return { kind: 'done', value };
    } catch (error) {
      if (!timedOut && isCancelError(error)) {
        return { kind: 'cancelled' };
      }
      const status = timedOut ? undefined : statusOf(error);
      const retryable = status === undefined || status >= 500;
      const maybeSent = bodyComplete || completionUnknown;
      if (!retryable || maybeSent || attempt > retryDelaysMs.length) {
        return { kind: 'failed', error, status, maybeUploaded: retryable && maybeSent };
      }
    } finally {
      clearTimeout(timer);
    }
  }
};
